<template>
  <div class="bound config-body">
    <!-- Headline, renders in portal at top of wizard.twig -->
    <portal to="headline">
      <h4 class="title" v-if="isCurrentView('intro')">Welcome!</h4>
      <h4 class="title" v-else-if="isCurrentView('summary')">Summary</h4>

      <step-counter
        v-else
        :current="currentStepCount"
        :count="product.steps.length"
      />
    </portal>

    <!-- Introductory View -->
    <!-- <intro-view
      v-if="isCurrentView('intro')"
      :product="product"
      :intro="introduction.intro"
      :outro="introduction.outro"
      @set-pointer-to-zero="pointer = 0"
    /> -->

    <!-- Summary View -->
    <summary-view
      v-if="isCurrentView('summary')"
      :product="product"
      :selections="selections"
      :clipboard-id="clipboardId"
      :running-total="runningTotal"
      :currency-formatter="currencyFormatter"
      :pending-save-request="pendingSaveRequest"
      :showConfiguratorPricing="userInViewPricingGroup"
      @save="submitClipboardRecord()"
      @completed="complete()"
    />

    <!-- Configuration Wizard -->
    <div class="configurator-steps" v-else>
      <!-- Sku Navigator -->
      <sku-navigator
        :product="product"
        :pointer="pointer"
        :selections="selections"
        @step-to="stepTo"
      />

      <div class="step_body">
        <!-- Steps Navigator -->
        <steps-navigator
          :title="product.title"
          :isLastStep="isLastStep"
          :canStepBack="canStepBack"
          :runningTotal="runningTotal"
          :canStepForward="canStepForward"
          :family="product.familyAssociation"
          :completeDisabled="completeDisabled"
          :showConfiguratorPricing="userInViewPricingGroup"
          @step-forward="setPointerForward()"
          @step-backward="setPointerBackward()"
        />

        <!-- Steps Options Lists -->
        <step-options-list
          :pointer="pointer"
          :step="currentStep"
          :selection="selection"
          :currency-formatter="currencyFormatter"
          :showConfiguratorPricing="userInViewPricingGroup"
          @step-option-selected="setSelectedOptions"
        />
      </div>

      <!-- Step Sidebar (Educational Content) -->
      <step-sidebar
        v-if="currentStep.educationalEntry"
        :title="currentStep.educationalEntry.title"
        :content="currentStep.educationalEntry.content"
        :toggle-image="currentStep.educationalEntry.toggleImage"
      />
    </div>

    <!-- Pop-up "Don't forget to save" Modal, renders in portal at bottom of _template-base.twig -->
    <confirm-dialog
      v-if="saveModalOpen"
      cancel-text="Cancel"
      confirm-text="Save SKU"
      :loading="loading"
      @confirm="submitClipboardRecord()"
      @cancel="saveModalOpen = false"
    >
      <h3>Save SKU?</h3>
      <p>
        Any progress will be lost if you leave without saving. Would you like to
        save your SKU?
      </p>
    </confirm-dialog>

    <!-- Footer links, renders in portal at bottom of wizard.twig -->
    <portal to="footer-actions">
      <ul v-if="isCurrentView('wizard')">
        <li v-if="deviceCanShare">
          <share-sku-button :sku-string="skuString" />
        </li>
        <li>
          <copy-button :value="skuString">Copy SKU</copy-button>
        </li>
        <li>
          <button class="btn_restart" type="button" @click="restart()">
            Start Over
          </button>
        </li>
      </ul>
    </portal>
  </div>
</template>

<script>
import axios from "axios";
import IntroView from "./wizard/IntroView";
import SummaryView from "./wizard/SummaryView";
import StepSidebar from "./wizard/StepSidebar";
import StepsNavigator from "./wizard/StepsNavigator";
import StepOptionsList from "./wizard/StepOptionsList";
import ShareSkuButton from "./wizard/ShareSkuButton";
import SkuNavigator from "./wizard/SkuNavigator";
import StepCounter from "./wizard/StepCounter";
import CopyButton from "./CopyButton";
import ConfirmDialog from "./ConfirmDialog";
import LoadingSpinner from "../../atoms/loading_spinner.js";
import currencyFormatter from "../../currency_formatter.js";

export default {
  components: {
    IntroView,
    StepsNavigator,
    StepOptionsList,
    StepSidebar,
    SkuNavigator,
    ShareSkuButton,
    SummaryView,
    StepCounter,
    ConfirmDialog,
    LoadingSpinner,
    CopyButton,
  },
  props: [
    "entry",
    "product",
    "preselect",
    "introduction",
    "clipboardIdFromTemplate",
  ],
  data() {
    return {
      clipboardId: this.clipboardIdFromTemplate, // can not modify props.
      loading: false,
      // pointer: this.clipboardIdFromTemplate ? 0 : -1,
      pointer: 0,
      selections: {}, // cannot modify props.
      selection: null,
      csrfTokenName: window.Craft.csrfTokenName,
      csrfTokenValue: window.Craft.csrfTokenValue,
      pendingSaveRequest: false,
      promptSaveOnUnload: false,
      saveModalOpen: false,
      hashSetForSummary: false,
      gaDataLayer: [],
      runningTotal: this.product.productBasePrice ?? 0,
      userInViewPricingGroup: window.hasOwnProperty("showConfiguratorPricing") ? window.showConfiguratorPricing: false,
      currencyFormatter,
    };
  },
  mounted() {
    this.gaDataLayer = window.dataLayer || [];
    this.selections =
      this.getFromPreset().length !== 0
        ? this.getFromPreset()
        : this.getAutoSaveObject(this.product.slug);
    this.validateRules();
    this.applyRules();

    const conditionallyPromptToSave = (e) => {
      if (this.promptSaveOnUnload) {
        (e || window.event).returnValue = "Changes you made may not be saved.";
        this.saveModalOpen = true;
      }
      return;
    };

    const hidePromptToSave = (e) => {
      this.saveModalOpen = false;
    };

    const saveOnCtrlSKeyCombination = (e) => {
      if (
        (window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) &&
        e.keyCode == 83
      ) {
        e.preventDefault();
        this.submitClipboardRecord();
      }
    };

    const viewSummaryRouter = () => {
      this.hashSetForSummary =
        location.hash === "#summary" || location.hash === "#print";
    };

    viewSummaryRouter();

    window.addEventListener("beforeunload", conditionallyPromptToSave);
    window.addEventListener("unload", hidePromptToSave);
    window.addEventListener("keydown", saveOnCtrlSKeyCombination);
    window.addEventListener("hashchange", viewSummaryRouter);

    this.$once("hook:destroyed", () => {
      window.removeEventListener("beforeunload", conditionallyPromptToSave);
      window.removeEventListener("unload", hidePromptToSave);
      window.removeEventListener("keydown", saveOnCtrlSKeyCombination);
      window.removeEventListener("hashchange", viewSummaryRouter);
    });
  },
  watch: {
    pointer: {
      handler: function(value) {
        this.selection = this.selections[this.currentStep.id];
        this.setAnalyticsCustomVars();
      },
    },
    selections: {
      deep: true,
      handler: function(value) {
        this.setAutoSaveObject(value);
        this.selection = value[this.currentStep.id];
      },
    },
  },
  computed: {
    steps() {
      return this.product.steps;
    },
    currentStep() {
      return this.steps[this.pointer] ? this.steps[this.pointer] : {};
    },
    currentStepCount() {
      return this.pointer + 1;
    },
    totalSteps() {
      return this.steps.length;
    },
    isLastStep() {
      return this.pointer === this.steps.length - 1;
    },
    canStepForward() {
      if (!this.userInViewPricingGroup) {
        return true;
      }

      const hasNotSureOption = this.currentStep.addNotSureOption;
      const currentSelection = this.selections[this.currentStep.id];
      const hasNextStep = this.steps[this.pointer + 1];

      return hasNotSureOption && hasNextStep || currentSelection && currentSelection.external !== "???";
    },
    completeDisabled() {
      const hasNextStep = this.steps[this.pointer + 1];
      const hasNotSureOption = this.currentStep.addNotSureOption;
      const currentSelection = this.selections[this.currentStep.id];
      const currentSelectionEmpty = !currentSelection || currentSelection.external === "???";

      return (
        hasNextStep || (!hasNotSureOption && currentSelectionEmpty)
      );
    },
    canStepBack() {
      return this.steps[this.pointer - 1] !== undefined;
    },
    currentView() {
      if (this.hashSetForSummary) {
        return "summary";
      } else if (this.pointer == -1 && !this.clipboardIdFromTemplate) {
        return "intro";
      } else {
        return "wizard";
      }
    },
    skuString() {
      let string = `${this.product.family}-${this.product.model}-${this.product.type}`;

      this.product.steps.forEach((step) => {
        let value = this.selections[step.id] ? this.selections[step.id].external : "???";
        string = string.concat(`-${value}`);
      });

      return string;
    },
    initialSku() {
      return `${this.product.family}-${this.product.model}-${this.product.type}`;
    },
    backUrl() {
      let url = localStorage.getItem("configRedirectBack");
      return url ? url : `/product/${this.product.slug}/configure`;
    },
    deviceCanShare: function() {
      try {
        return navigator.share;
      } catch {
        return false;
      }
    },
  },
  methods: {
    submitClipboardRecord() {
      this.loading = true;
      this.pendingSaveRequest = true;

      let endpoint = `save/${this.clipboardId ? this.clipboardId : ""}`;

      const selections = Object.keys(this.selections).reduce((acc, stepId) => {
        let selection = this.selections[stepId];
        acc[stepId] = { internal: selection.internal, external: selection.external };
        return acc;
      }, {});

      axios
        .post(`/configurator-module/api/clipboard/${endpoint}`, {
          [this.csrfTokenName]: this.csrfTokenValue,
          productId: this.product.id,
          selections: selections,
        })
        .then((response) => {
          this.$emit("refresh-clipboard");
          this.saveModalOpen = false;
          this.promptSaveOnUnload = false;
          if (!this.clipboardId) {
            this.clipboardId = response.data.id;
          }
        })
        .catch(function(error) {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
          this.pendingSaveRequest = false;
        });
    },
    setAnalyticsCustomVars() {
      if (this.pointer > 0) {
        this.gaDataLayer.push({
          event: "configurator-step",
          "configurator-step-number": this.pointer + 1,
          "configurator-step-sku": this.skuString,
        });
      }
    },
    setPointerForward() {
      if (this.canStepForward) {
        this.pointer += 1;
      }
    },
    setPointerBackward() {
      if (this.canStepBack) {
        const currentStep = this.steps[this.pointer];

        for (const option of currentStep.options) {
          if (option.sku === this.selections[currentStep.id]) {
            this.runningTotal -= option.price;

            delete this.selections[currentStep.id];
          }
        }

        this.pointer -= 1;
      }
    },
    stepTo(pointer) {
      if (this.product.steps[pointer] !== undefined) {
        this.pointer = pointer;
      }
    },
    complete() {
      this.clearSelections();
      this.resetPointer();
      this.promptSaveOnUnload = false;
      window.location.href = this.backUrl;
    },
    restart() {
      this.clearSelections();
      this.resetPointer();
      window.location.href = `/product/${this.product.slug}/configure`;
    },
    clearSelections() {
      this.selections = {};
    },
    resetPointer() {
      this.pointer = 0;
    },
    setSelectedOptions(stepId, skuModel, price) {
      const previousSelection = this.selections[stepId];

      const unsureOptionSelected =
        previousSelection &&
        previousSelection.internal === "???" &&
        skuModel.internal === "???";

      /* subtracts _existing_ option price before adding the new option price */
      if (previousSelection && !unsureOptionSelected) {
        const currentStep = this.steps.find((step) => step.id === stepId);
        const currentOption = currentStep.options.find(
          (option) => option.internal_id === previousSelection.internal
        );

        if (currentOption && currentOption.price) {
          this.runningTotal -= currentOption.price;
        }
      }

      if (skuModel.internal !== "???") {
        this.runningTotal += price;
      }

      this.selections = Object.assign({}, this.selections, { [stepId]: skuModel });
      this.applyRules();
    },
    validateRules() {
      let validationMap = {};

      // check for duplicates
      this.product.stepRules.forEach((item) => {
        if (!validationMap[item.targetStepId])
          validationMap[item.targetStepId] = {};
        let ruleKey = item.sourceStepId + "-" + item.sourceSku;

        if (validationMap[item.targetStepId][ruleKey]) {
          console.warn(
            `Duplicate rules for target step ${item.targetStepId}: ${ruleKey}`
          );
        } else {
          validationMap[item.targetStepId][ruleKey] = true;
        }
      });

      validationMap = {};
      let orderIndex = 0;

      // check for more than one source step referencing same target step
      // each step targeted may have at most two source steps: one inclusive and one exclusive
      // assign index value for sorting later
      this.product.stepRules.forEach((item) => {
        item.orderIndex = orderIndex++;
        let targetKey = `${item.targetStepId}-${item.disallowSkusMode}`;
        if (!validationMap[targetKey]) validationMap[targetKey] = {};
        let ruleKey = item.sourceStepId;

        validationMap[targetKey][ruleKey] = true;
      });

      Object.keys(validationMap).forEach((targetStepId) => {
        let targetStep = validationMap[targetStepId];
        if (Object.keys(targetStep).length > 1) {
          console.warn(
            `Multiple source steps targeting step ${targetStepId}`,
            targetStep
          );
        }
      });
    },
    applyRules() {
      // reset allowed for all step options
      this.product.steps.forEach((step) => {
        step.options.forEach((option) => {
          option.allowed = true;
        });
      });

      // sort allow rules first, then by orderIndex (order in CMS)
      const byDisallowedAndStepNumber = (a, b) => {
        if (a.disallowSkusMode < b.disallowSkusMode) {
          return -1;
        }
        if (a.disallowSkusMode > b.disallowSkusMode) {
          return 1;
        }

        // sort on orderIndex
        if (a.orderIndex < b.orderIndex) {
          return -1;
        }
        if (a.orderIndex > b.orderIndex) {
          return 1;
        }

        return 0;
      };

      // iterate all selections, compiling rules, sort, then apply
      let applicableRules = [];

      for (const [stepId, selectedSku] of Object.entries(this.selections)) {
        let applicableRulesThisStep = this.product.stepRules.filter((rule) => {
          // need to maintain backwards compatibility with old rules
          // new rules use internalId to track source sku
          if (rule.sourceInternalId !== null) {
            return (
              rule.sourceStepId === parseInt(stepId) &&
              rule.sourceInternalId === selectedSku.internal
            )
          } else {
            return (
              rule.sourceStepId === parseInt(stepId) &&
              rule.sourceSku === selectedSku.external
            );
          }
        });

        applicableRules.push(...applicableRulesThisStep);
      }

      applicableRules.sort(byDisallowedAndStepNumber);

      // apply rules
      applicableRules.forEach((rule) => {
        let targetStep = this.product.steps.find(
          (step) => step.id === rule.targetStepId
        );

        if (targetStep) {
          let currentSelection = this.selections[rule.targetStepId];
          let v2method = rule.sourceInternalId !== null;

          targetStep.options.forEach((option) => {
            let identifier = (v2method ? option.internal_id : option.sku) + "";

            if (rule.disallowSkusMode) {
              if (option.allowed) {
                option.allowed = !rule.targetSkus.includes(identifier);
              } else {
                // don't allow toggling "on" items with exclusion rules
              }
            } else {
              option.allowed = rule.targetSkus.includes(identifier);
            }

            if (!option.allowed && currentSelection === identifier) {
              delete this.selections[rule.targetStepId];
            }
          });
        }
      });
    },
    getFromPreset() {
      return this.preselect;
    },
    setAutoSaveObject(value) {
      localStorage.setItem(
        "configAutoSave." + this.product.slug,
        JSON.stringify(value)
      );
    },
    getAutoSaveObject(slug) {
      return {}; // Temporarily disable auto save object retrieval.
      let string = localStorage.getItem(`configAutoSave.${slug}`);
      return JSON.parse(!string || string.length === 0 ? "[]" : string);
    },
    isCurrentView(string = "") {
      return this.currentView === string;
    },
  },
};
</script>
